/**
 * S3 upload helpers that request signed URLs through GraphQL.
 */
import { isNetworkError, isOnline } from "offline/network";
import { enqueueS3 } from "offline/queue";
import { postGql } from "offline/gqlClient";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * Builds the public S3 object key for a site directory and filename.
 *
 * @param {string} directory Upload folder.
 * @param {string} filename Object filename.
 * @returns {string}
 */
const objectKeyFromName = (directory, filename) => {
  const prefix = directory ? `${process.env.REACT_APP_SITE_NAME}/${directory}` : process.env.REACT_APP_SITE_NAME;
  return `${prefix}/${filename}`;
};

/**
 * Uploads a file to S3 via a signed URL, optionally deleting the previous object.
 * When offline, the file is stored in IndexedDB and a blob URL is returned for preview.
 *
 * @param {File} inputFile Browser file to upload.
 * @param {string} directory Target folder.
 * @param {string} newFileName Destination filename.
 * @param {string} [oldFileName] Previous filename to delete.
 * @param {{ skipQueue?: boolean }} [options] Flush path must set skipQueue.
 * @returns {Promise<string>} Public URL of the uploaded object, or a blob URL while queued.
 */
export const uploadToS3 = (inputFile, directory, newFileName, oldFileName, options = {}) =>
  new Promise((resolve, reject) => {
    if (inputFile && inputFile.size > MAX_UPLOAD_BYTES) {
      return reject("Filesize is too big");
    }

    /**
     * Persists the file bytes and returns a local object URL for the UI.
     *
     * @returns {Promise<string>}
     */
    const queueUpload = async () => {
      const buffer = await inputFile.arrayBuffer();
      const localUrl = URL.createObjectURL(inputFile);
      await enqueueS3({
        directory,
        newFileName,
        oldFileName: oldFileName || "",
        contentType: inputFile.type || "application/octet-stream",
        fileName: inputFile.name || newFileName,
        buffer,
        localUrl,
      });
      return localUrl;
    };

    (async () => {
      if (!options.skipQueue && !isOnline()) {
        resolve(await queueUpload());
        return;
      }

      try {
        if (oldFileName) {
          try {
            await postGql(
              `mutation s3_delete_object($key: String, $filename: String, $directory: String) {
                s3_delete_object(key: $key, filename: $filename, directory: $directory)
              }`,
              { filename: oldFileName, directory }
            );
          } catch (err) {
            if (process.env.NODE_ENV !== "production") console.log("[delete err]", err);
          }
        }

        const ticket = await postGql(
          `mutation s3_upload_url($directory: String, $filename: String!, $content_type: String) {
            s3_upload_url(directory: $directory, filename: $filename, content_type: $content_type) {
              upload_url
              public_url
            }
          }`,
          {
            directory,
            filename: newFileName,
            content_type: inputFile.type || "application/octet-stream",
          }
        );

        const putRes = await fetch(ticket.upload_url, {
          method: "PUT",
          body: inputFile,
          headers: {
            "Content-Type": inputFile.type || "application/octet-stream",
          },
        });
        if (!putRes.ok) {
          const detail = await putRes.text();
          throw new Error(detail || "S3 upload failed");
        }
        resolve(ticket.public_url || `${process.env.REACT_APP_S3_ENDPOINT}/${objectKeyFromName(directory, newFileName)}`);
      } catch (err) {
        if (!options.skipQueue && isNetworkError(err)) {
          resolve(await queueUpload());
          return;
        }
        reject(err);
      }
    })().catch(reject);
  });
